<template>
  <div
    class="root"
    :class="[ `viewer-${apiConfig.viewerMode}` ]"
    ref="fullscreenRoot"
  >
    <ZoomOverlay
      v-if="active && zoomed && meta && apiConfig.zoomEnabled"
      class="zoom-overlay"
      alt="gallery"
      :media="active.src"
      :current-index="current"
      :total-media="apiConfig.fileUrls.length"
      :mime-type="meta?.mime"
      :aspect-ratio="aspectRatio"
      :outer-video="videoRef"
      @next="next"
      @prev="prev"
      @close="zoomed = false"
    />
    <div
      v-if="apiConfig.viewerMode === 'single' && toolbarStore.topItems.length > 0"
      class="single-toolbar-slot single-toolbar-slot-top"
    >
      <TopBar
        class="buttons top-bar"
        :visibility-override="topBarVisibility"
        :current="current"
        :preloaded-images="preloadedImages"
        :preloaded-meta="preloadedMeta"
        :show-urls-and-votes="showUrlsAndVotes"
        :image-size-restrain-mode="imageSizeRestrainMode"
        :is-visible="true"
        :autoplay="autoplay"
        @next="next"
        @prev="prev"
        @goto="goto"
        @zero="goto(0); lowerBound = 0; upperBound = 0"
        @reset="reset"
        @fullscreen="fullscreen"
        @autoplay="(state: boolean) => autoplay = state"
        @open-vote-overlay="overlayVote(null)"
        @open-tag-overlay="showBigTagAdd = !showBigTagAdd"
        @show-meta-overlay="(state: boolean) => showUrlsAndVotes = state"
        @next-image-size-restrain-mode="nextImageSizeRestrainMode()"
        @toggle-request-stats="showRequestStats = !showRequestStats"
      />
    </div>
    <div
      v-if="apiConfig.viewerMode === 'single'"
      class="image-wrapper"
      ref="swipeable"
      :class="constrainClass"
      @dblclick="apiConfig.mouseVotes && overlayVote(null)"
    >
      <div
        class="sidebar-overlay-wrapper"
        v-if="showUrlsAndVotes"
      >
        <div
          class="sidebar-overlay-side right"
        >
          <div
            class="favs sidebar-overlay"
          >
            <MetaRating
              :active-ratings="activeRatings"
              @rate="(key, newRating) => rate(fileId, key, newRating)"
            />
          </div>
          <div
            class="sidebar-overlay"
            v-if="!showRequestStats"
          >
            <HydrusFetchTracker
              class="fetch-tracker"
            />
          </div>
        </div>
        <div
          class="sidebar-overlay-side left"
        >
          <div
            class="urls sidebar-overlay"
          >
            <MetaUrls
              v-if="meta?.known_urls && active !== undefined"
              :urls="meta.known_urls"
              :source="active"
            />
            <div
              v-else
            >
              Urls not yet loaded.
            </div>
          </div>
          <div
            class="tags sidebar-overlay tag-add"
          >
            <MetaTagAdd
              v-if="hasTags"
              @add="(tag) => sendTagAddition(fileId, tag)"
            />
            <div
              v-else
            >
              Tags not yet loaded.
            </div>
          </div>
          <div
            class="tags sidebar-overlay tag-display"
          >
            <MetaTags
              v-if="hasTags && meta !== null"
              :meta="meta"
              @add="(tag) => sendTagAddition(fileId, tag)"
              @delete="(tag) => sendTagDeletion(fileId, tag)"
            />
            <div
              v-else
            >
              Tags not yet loaded.
            </div>
          </div>
        </div>
      </div>
      <div
        class="image-wrapper-inner"
      >
        <BlurhashCanvas
          v-if="showSingleLoader && isImage && meta?.blurhash"
          :hash="meta.blurhash"
          class="single-media-placeholder"
          aria-hidden="true"
        />
        <img
          v-else-if="showSingleLoader && isImage && singleThumbnailSrc"
          class="single-media-placeholder"
          :src="singleThumbnailSrc"
          alt=""
          aria-hidden="true"
        >
        <div
          v-if="showSingleLoader"
          class="single-media-loader"
        >
          <span
            class="single-media-spinner"
            aria-label="Loading media…"
          />
          <span
            class="single-media-loader-text"
          >
            {{ meta === null ? "Loading metadata…" : "Loading media…" }}
          </span>
        </div>
        <img
          v-if="active && isImage && preloaded?.loaded.value"
          alt="gallery"
          :src="active.src"
          @click="mediaGotClicked()"
          class="media-element"
        >
        <video
          :key="active.src"
          loop
          v-else-if="active && !isImage"
          controls
          autoplay
          @click="mediaGotClicked()"
          preload="metadata"
          ref="videoRef"
          class="media-element"
          :class="constrainClass"
        >
          <source
            :src="active.src"
            :type="meta?.mime || 'video/mp4'"
          >
          gallery video
        </video>
        <span
          v-else-if="!showSingleLoader"
          class="single-media-unavailable"
        >
          Media unavailable.
        </span>
      </div>
      <div
        class="fetch-tracker-overlay"
        v-if="showRequestStats"
      >
        <HydrusFetchTracker
          class="fetch-tracker"
        />
      </div>
    </div>
    <div
      v-if="apiConfig.viewerMode === 'single' && toolbarStore.bottomItems.length > 0"
      class="single-toolbar-slot single-toolbar-slot-bottom"
    >
      <SettingsPane
        class="buttons bottom-bar"
        :visibility-override="bottomBarVisibility"
        :current="current"
        :preloaded-images="preloadedImages"
        :preloaded-meta="preloadedMeta"
        :show-urls-and-votes="showUrlsAndVotes"
        :image-size-restrain-mode="imageSizeRestrainMode"
        :is-visible="true"
        :autoplay="autoplay"
        @next="next"
        @prev="prev"
        @goto="goto"
        @zero="goto(0); lowerBound = 0; upperBound = 0"
        @reset="reset"
        @fullscreen="fullscreen"
        @autoplay="(state: boolean) => autoplay = state"
        @open-vote-overlay="overlayVote(null)"
        @open-tag-overlay="showBigTagAdd = !showBigTagAdd"
        @show-meta-overlay="(state: boolean) => showUrlsAndVotes = state"
        @next-image-size-restrain-mode="nextImageSizeRestrainMode()"
        @toggle-request-stats="showRequestStats = !showRequestStats"
      />
    </div>
    <FloatingBarShell
      v-if="showFloatingTopBar"
      edge="top"
      :mode="topFloatingMode"
      :reveal-px="scrollTopBarRevealPx"
      @height-change="onTopFloatingBarHeightChange"
    >
      <TopBar
        class="buttons top-bar"
        :visibility-managed-by-shell="true"
        :visibility-override="topBarVisibility"
        :current="current"
        :preloaded-images="preloadedImages"
        :preloaded-meta="preloadedMeta"
        :show-urls-and-votes="showUrlsAndVotes"
        :image-size-restrain-mode="imageSizeRestrainMode"
        :is-visible="true"
        :autoplay="autoplay"
        @next="next"
        @prev="prev"
        @goto="goto"
        @zero="goto(0); lowerBound = 0; upperBound = 0"
        @reset="reset"
        @fullscreen="fullscreen"
        @autoplay="(state: boolean) => autoplay = state"
        @open-vote-overlay="overlayVote(null)"
        @open-tag-overlay="showBigTagAdd = !showBigTagAdd"
        @show-meta-overlay="(state: boolean) => showUrlsAndVotes = state"
        @next-image-size-restrain-mode="nextImageSizeRestrainMode()"
        @toggle-request-stats="showRequestStats = !showRequestStats"
      />
    </FloatingBarShell>
    <FloatingBarShell
      v-if="showFloatingBottomBar"
      edge="bottom"
      :mode="bottomFloatingMode"
      :reveal-px="scrollBottomBarRevealPx"
      @height-change="onBottomFloatingBarHeightChange"
    >
      <SettingsPane
        class="buttons"
        :visibility-managed-by-shell="true"
        :visibility-override="bottomBarVisibility"
        :current="current"
        :preloaded-images="preloadedImages"
        :preloaded-meta="preloadedMeta"
        :show-urls-and-votes="showUrlsAndVotes"
        :image-size-restrain-mode="imageSizeRestrainMode"
        :is-visible="true"
        :autoplay="autoplay"
        @next="next"
        @prev="prev"
        @goto="goto"
        @zero="goto(0); lowerBound = 0; upperBound = 0"
        @reset="reset"
        @fullscreen="fullscreen"
        @autoplay="(state: boolean) => autoplay = state"
        @open-vote-overlay="overlayVote(null)"
        @open-tag-overlay="showBigTagAdd = !showBigTagAdd"
        @show-meta-overlay="(state: boolean) => showUrlsAndVotes = state"
        @next-image-size-restrain-mode="nextImageSizeRestrainMode()"
        @toggle-request-stats="showRequestStats = !showRequestStats"
      />
    </FloatingBarShell>
    <ul
      v-if="apiConfig.viewerMode !== 'single'"
      class="image-slides"
      :style="scrollViewerStyle"
      v-horizontal-scroll="horizontalScrollOptions"
      v-prevent-browser-gestures="scrollGestureMode"
      @wheel.passive="onScrollGestureWheel"
      @touchstart.passive="onScrollGestureTouchStart"
      @touchmove.passive="onScrollGestureTouchMove"
      @touchend.passive="onScrollGestureTouchEnd"
      @touchcancel.passive="onScrollGestureTouchEnd"
    >
      <li
        v-if="showInlineTopBar"
        class="slide-toolbar slide-toolbar-top"
      >
        <TopBar
          class="buttons top-bar"
          :visibility-override="topBarVisibility"
          :stacked="apiConfig.viewerMode === 'desktop'"
          :current="current"
          :preloaded-images="preloadedImages"
          :preloaded-meta="preloadedMeta"
          :show-urls-and-votes="showUrlsAndVotes"
          :image-size-restrain-mode="imageSizeRestrainMode"
          :is-visible="true"
          :autoplay="autoplay"
          @next="next"
          @prev="prev"
          @goto="goto"
          @zero="goto(0); lowerBound = 0; upperBound = 0"
          @reset="reset"
          @fullscreen="fullscreen"
          @autoplay="(state: boolean) => autoplay = state"
          @open-vote-overlay="overlayVote(null)"
          @open-tag-overlay="showBigTagAdd = !showBigTagAdd"
          @show-meta-overlay="(state: boolean) => showUrlsAndVotes = state"
          @next-image-size-restrain-mode="nextImageSizeRestrainMode()"
          @toggle-request-stats="showRequestStats = !showRequestStats"
        />
      </li>
      <MediaMetaSlideGroup
        v-for="(source, index) in scrollRenderableSources"
        :key="source.id"
        ref="mediaLiLiRefs"
        :source="source"
        :media="committedScrollMeta[source.id]"
        :meta="committedScrollMeta[source.id]"
        :active-ratings="preloadedRatings[source.id] ?? {}"
        :is-mobile="apiConfig.viewerMode === 'mobile'"
        :is-visible="imageVisibilityFlags[index]"
        :is-current="index === current"
        :can-reveal-resolved-media="isScrollSlideNearViewport(index)"
        :show-blocking-loader="activeScrollLoaderIndex === index"
        :can-skip-loader="activeScrollLoaderIndex === index && index < apiConfig.fileUrls.length - 1"
        @add="(tag) => sendTagAddition(source.id, tag)"
        @delete="(tag) => sendTagDeletion(source.id, tag)"
        @rate="(key, newRating) => rate(source.id, key, newRating)"
        @media-clicked="goto(index); zoomed = true"
        @presentation-ready-change="(ready) => setScrollSlideReady(source.id, ready)"
        @skip-loader="skipScrollLoader(source.id)"
      />
      <li
        v-if="showInlineBottomBar"
        class="slide-toolbar slide-toolbar-bottom"
      >
        <SettingsPane
          :visibility-override="bottomBarVisibility"
          :current="current"
          :preloaded-images="preloadedImages"
          :preloaded-meta="preloadedMeta"
          :show-urls-and-votes="showUrlsAndVotes"
          :image-size-restrain-mode="imageSizeRestrainMode"
          :is-visible="true"
          :scroll-pane="true"
          :autoplay="autoplay"
          @next="next"
          @prev="prev"
          @goto="goto"
          @zero="goto(0); lowerBound = 0; upperBound = 0"
          @reset="reset"
          @fullscreen="fullscreen"
          @autoplay="(state: boolean) => autoplay = state"
          @open-vote-overlay="overlayVote(null)"
          @open-tag-overlay="showBigTagAdd = !showBigTagAdd"
          @show-meta-overlay="(state: boolean) => showUrlsAndVotes = state"
          @next-image-size-restrain-mode="nextImageSizeRestrainMode()"
          @toggle-request-stats="showRequestStats = !showRequestStats"
        />
      </li>
    </ul>
    <VoteOverlay
      v-if="showVoteOverlay && apiConfig.services"
      @upvote="upvote()"
      @downvote="downvote()"
      @close="cancelVote()"
      @save="submitVote()"
      @prev="prev(); cancelVote()"
      @next="next(); cancelVote()"
      :votable-ratings="votableRatings"
      :is-submitting="isSubmittingVote"
      :spinner-direction="spinnerDirection"
      :show-preview="showVotePreview"
      :preview-src="votePreview.src"
      :preview-blurhash="votePreview.blurhash"
      :preview-is-loading="votePreview.isLoading"
    />
    <TagOverlay
      v-if="showBigTagAdd"
      :has-tags="hasTags"
      @close="showBigTagAdd = false"
      @add="(tag: string) => sendTagAddition(fileId, tag)"
    />

    <!-- Network error toast with retry capability -->
    <RetryableErrorToast
      v-if="networkErrorToastVisible && currentRetryPolicy"
      :title="'Connection Error'"
      :message="networkErrorMessage"
      :details="networkErrorDetails"
      :retry-policy="currentRetryPolicy"
      :dismissible="true"
      :show-attempt-info="true"
      @retry="reload"
      @dismissed="networkErrorToastVisible = false"
      @change-host="router.push('/login')"
      @retry-cancelled="networkErrorToastVisible = false"
    />
  </div>
</template>

<script setup lang="ts">
import {Key} from 'ts-key-enum';
import {computed, onMounted, onUnmounted, ref, unref, watch, watchEffect} from 'vue';
import {useCycleList} from '@vueuse/core';
import type {Image as ImageDataType} from '@/stores/api';
import {SpecialPage, useApiStore} from '@/stores/api';
import {w} from '@/libraries/events';
import type {
  ImageMetadata,
  Metadata,
  ServiceHash,
} from '@/stores/ImageMetadata';
import {TagApiAction} from '@/stores/ImageMetadata';
import {buildUrlWithParams} from '@/requests/url';
import type {
  AnyRatingServiceIncludingValue,
  AnyValue,
} from '@/stores/ratingService';
import {
  isCounterServiceIncludingValue,
  isLikeServiceIncludingValue,
  isStarsServiceIncludingValue,
  RatingServiceType,
} from '@/stores/ratingService';
import {ReactiveImage} from '@/libraries/vue-ractive-new-html-image/ref-img';
import {useSwipeable} from '@/libraries/react-swipable/vue/main';
import screenfull from 'screenfull';
import VoteOverlay from '@/components/VoteOverlay.vue';
import MetaUrls from '@/components/meta/MetaUrls.vue';
import MetaTags from '@/components/meta/MetaTags.vue';
import type {ActiveRating, TagMeta} from '@/components/meta/types';
import MetaRating from '@/components/meta/MetaRating.vue';
import MetaTagAdd from '@/components/meta/MetaTagAdd.vue';
import SettingsPane from '@/components/SettingsPane.vue';
import TopBar from '@/components/TopBar.vue';
import FloatingBarShell from '@/components/FloatingBarShell.vue';
import MediaMetaSlideGroup from '@/components/slides/MediaMetaSlideGroup.vue';
import type {MediaMetaSlideGroupExposed} from '@/components/slides/slideTypes';
import {hasTagsCheck} from '@/components/meta/helper';
import {computeArchivalDecision} from '@/utils/archival';
import {useFetch} from '@/libraries/vue-fetch/composables/useFetch';
import {useTrackedVideo} from '@/libraries/vue-fetch/composables/useTrackedVideo';
import HydrusFetchTracker from '@/components/HydrusFetchTracker.vue';
import TagOverlay from '@/components/TagOverlay.vue';
import ZoomOverlay from '@/components/ZoomOverlay.vue';
import BlurhashCanvas from '@/components/BlurhashCanvas.vue';
import {calculateAspectRatio} from '@/composables/AspectRatioRotator';
import {resolveBarVisibility, useToolbarStore} from '@/stores/toolbar';
import {
  buildViewerRouteQuery,
  getViewerRouteQueryKey,
  hasViewerRouteState,
  readViewerRouteState,
  resolveSelectedPageFromRouteToken,
  serializeSelectedPageForRoute,
} from '@/utils/viewerRoute';
import {useRoute, useRouter} from 'vue-router';
import {sleep} from '@/utils/sleep';
import type {SpinnerDirection} from '@/components/Svg/SvgSpinner.vue';
import {RetryPolicy, isNetworkError, getErrorMessage} from '@/utils/retry';
import RetryableErrorToast from '@/components/RetryableErrorToast.vue';

const apiConfig = useApiStore();
const toolbarStore = useToolbarStore();
const route = useRoute();
const router = useRouter();
const initialViewerRouteState = hasViewerRouteState(route.query)
  ? readViewerRouteState(route.query)
  : null;
console.log('fileUrls.value:', apiConfig.fileUrls);

const fullscreenRoot = ref<HTMLDivElement>();
const zoomed = ref<boolean>(false);
const scrollTopBarHeight = ref(0);
const scrollBottomBarHeight = ref(0);
const scrollTopBarRevealPx = ref(0);
const scrollBottomBarRevealPx = ref(0);
const lastScrollTouchY = ref<number | null>(null);

// Network error handling
const networkErrorToastVisible = ref(false);
const networkErrorMessage = ref('');
const networkErrorDetails = ref('');
const currentRetryPolicy = ref<RetryPolicy | null>(null);

function fullscreen(reference: HTMLElement | undefined = undefined) {
  // eslint-disable-next-line prettier/prettier
  const elem =
    reference === undefined
      ? fullscreenRoot.value
      // eslint-disable-line prettier/prettier
      // eslint-disable-next-line prettier/prettier
      : (reference === null ? undefined : reference);
  if (screenfull.isEnabled) {
    screenfull.toggle(elem);
  }
}

const swipeable = ref<HTMLImageElement>();
useSwipeable(swipeable, {
  preventScrollOnSwipe: true,
  trackMouse: true,
  onSwipedDown() {
    alert('down');
  },
});

const preloadedImages = ref<{ [key: string]: ReactiveImage | null }>({});
const preloadedMeta = ref<{ [key: number]: Metadata }>({});
const committedScrollMeta = ref<{ [key: number]: Metadata }>({});
const scrollSlideReady = ref<{ [key: number]: boolean }>({});
const skippedScrollLoaderIds = ref<number[]>([]);
// ratings of images is a separate array, so we can edit those as needed in the vote gui, not waiting for the api to re-fetch the result.
// eslint-disable-next-line prettier/prettier
const preloadedRatings = ref<{ [key: number]: null | { [key: ServiceHash]: AnyRatingServiceIncludingValue } }>({});

type ReasonatedIndexReason = 'scroll' | 'goto';
type ReasonatedIndex = {
  int: number;
  reason: ReasonatedIndexReason;
};

const reasonatedIndex = ref<ReasonatedIndex>({
  int: 0,
  reason: 'goto',
});
const index = computed<number>(() => reasonatedIndex.value.int);
const current = index;
const lowerBound = ref<number>(0);
const upperBound = ref<number>(0);
const active = computed<ImageDataType | undefined>(() => apiConfig.fileUrls[current.value]);
const fileId = computed<number>(() => active.value.id);
const meta = computed<Metadata | null>(() =>
  active.value && active.value.id in preloadedMeta.value
    ? preloadedMeta.value[active.value.id]
    : null,
);
const preloaded = computed<ReactiveImage | null>(() =>
  active.value && active.value.src in preloadedImages.value
    ? preloadedImages.value[active.value.src]
    : null,
);
const singleThumbnailSrc = computed(() => {
  if (!apiConfig.sessionKey || !active.value) return '';
  return buildUrlWithParams(apiConfig.host + '/get_files/thumbnail', {
    file_id: active.value.id.toString(),
    'Hydrus-Client-API-Session-Key': apiConfig.sessionKey,
  }).toString();
});
const isImage = computed<boolean>(() =>
  meta.value ? meta.value.mime.startsWith('image/') : true,
); // true if meta not yet loaded.
const aspectRatio = computed(() => calculateAspectRatio(meta.value?.width, meta.value?.height));
const activeRatings = computed<ActiveRating>(() => (active.value ? preloadedRatings.value[active.value.id] ?? {} : {}));
const showSingleLoader = computed(() =>
  active.value !== undefined &&
  (meta.value === null || (isImage.value && preloaded.value?.loaded.value !== true)),
);
const currentImageHash = computed<string | null>(() => meta.value?.hash ?? null);

type ViewerRouteStateSnapshot = {
  page: string | null;
  search: string;
  index: number;
  hash: string | null;
};

const currentViewerSelectionSignature = computed(() =>
  `${serializeSelectedPageForRoute(apiConfig.selectedPage) ?? ''}\n${apiConfig.searchQuery ?? ''}`,
);
const hasViewerSelectionState = computed(() =>
  apiConfig.selectedPage !== null ||
  (apiConfig.searchQuery ?? '') !== '' ||
  apiConfig.fileUrls.length > 0,
);

const viewerRouteState = computed<ViewerRouteStateSnapshot | null>(() => {
  if (!hasViewerSelectionState.value) {
    return null;
  }
  if (active.value === undefined && apiConfig.fileUrls.length > 0) {
    return null;
  }
  return {
    page: serializeSelectedPageForRoute(apiConfig.selectedPage),
    search: apiConfig.searchQuery ?? '',
    index: active.value === undefined ? 0 : current.value,
    hash: currentImageHash.value,
  };
});

const shouldSkipViewerRouteSync = ref(initialViewerRouteState !== null);
const loadedViewerSelectionSignature = ref<string | null>(null);
let lastObservedViewerRouteState: ViewerRouteStateSnapshot | null = null;
let lastViewerRouteCheckpointState: ViewerRouteStateSnapshot | null = null;
let lastViewerRouteCheckpointAt = 0;
let viewerRouteImagesSinceCheckpoint = 0;
const votableRatings = ref<AnyRatingServiceIncludingValue[]>([]);
type RatingOfService = {
  [key: ServiceHash]: AnyRatingServiceIncludingValue;
};

const showVoteOverlay = ref(false);
const isSubmittingVote = ref(false);

// Vote overlay preview thumbnail (for non-single viewer modes)
const votePreviewThumbnailImage = ref<ReactiveImage | null>(null);
watch(
  () => active.value?.src,
  () => {
    votePreviewThumbnailImage.value = null;
  },
);
watch(showVoteOverlay, (show) => {
  if (!show || apiConfig.viewerMode === 'single') {
    return;
  }
  if (!votePreviewThumbnailImage.value && singleThumbnailSrc.value) {
    const img = new ReactiveImage(undefined, undefined, meta.value?.blurhash ?? undefined);
    img.src = singleThumbnailSrc.value;
    votePreviewThumbnailImage.value = img;
  }
}, {immediate: true});
const votePreview = computed(() => {
  if (apiConfig.viewerMode === 'single') {
    return {
      src: null as string | null,
      blurhash: null as string | null,
      isLoading: false,
    };
  }
  if (isImage.value && preloaded.value?.loaded.value) {
    return {
      src: active.value!.src,
      blurhash: null as string | null,
      isLoading: false,
    };
  }
  if (singleThumbnailSrc.value) {
    const thumbnailImg = votePreviewThumbnailImage.value;
    if (thumbnailImg) {
      // displaySrc returns the blurhash data URL while loading, then the real thumbnail
      return {
        src: thumbnailImg.displaySrc.value,
        blurhash: null as string | null,
        isLoading: !thumbnailImg.loaded.value,
      };
    }
    return {
      src: null as string | null,
      blurhash: null as string | null,
      isLoading: true,
    };
  }
  if (isImage.value && meta.value?.blurhash) {
    // No thumbnail URL available; fall back to the raw blurhash string
    return {
      src: null as string | null,
      blurhash: meta.value.blurhash,
      isLoading: false,
    };
  }
  if (isImage.value) {
    return {
      src: null as string | null,
      blurhash: null as string | null,
      isLoading: true,
    };
  }
  return {
    src: null as string | null,
    blurhash: null as string | null,
    isLoading: false,
  };
});
const showVotePreview = computed(
  () => apiConfig.viewerMode !== 'single' &&
    (votePreview.value.src !== null || votePreview.value.blurhash !== null || votePreview.value.isLoading),
);

watch(showVoteOverlay, (value, oldValue) => {
  // if was open, now closed
  if (oldValue && !value) {
    // disable the animation for the next time.
    apiConfig.hasSeenVoteAnimation = true;
  }
});

const spinnerDirection = ref<SpinnerDirection>('clockwise');

function cloneRatings<T>(ratings: T): T {
  return JSON.parse(JSON.stringify(ratings));
}

const writeVotableRatings = () => {
  if (showVoteOverlay.value) {
    return; // don't change while the overlay is open.
  }
  const activeImage = active.value;
  votableRatings.value = cloneRatings(
    // eslint-disable-next-line prettier/prettier
    activeImage === undefined || preloadedRatings.value[activeImage.id] === undefined
      ? []
      : apiConfig.voteServiceIds
        .map(
          (key: ServiceHash) =>
            ((preloadedRatings.value[activeImage.id] as RatingOfService) ?? {})[key],
        )
        .filter((item) => item !== undefined),
  );
};
watchEffect(writeVotableRatings);

const hasTags = computed(() => hasTagsCheck(meta.value, apiConfig.tagReadServiceId));

const showBigTagAdd = ref<boolean>(false);
const showRequestStats = ref<boolean>(false);
const showUrlsAndVotes = ref<boolean>(true);
const IMAGE_RESTRAIN_MODES = ['limited-by-space', 'scale-to-fit', 'width-fit', 'height-fit', 'original'];
const {
  state: constrainClass,
  next: nextImageSizeRestrainMode,
  index: imageSizeRestrainMode,
} = useCycleList(IMAGE_RESTRAIN_MODES);

const autoplay = ref<boolean>(false);

async function loadMetaForImageIds(imageIds: number[]) {
  if (!imageIds || imageIds.length === 0) {
    console.log('NOT requesting meta for images, because empty:', imageIds);
    return;
  }

  console.log('requesting meta for images:', imageIds);
  if (apiConfig.sessionKey === null) {
    throw Error("sessionKey can't be null");
  }
  if (apiConfig.voteServiceIds === null) {
    throw Error("voteServiceIds can't be null");
  }
  // eslint-disable-next-line prettier/prettier
  const url = buildUrlWithParams(
    apiConfig.host + '/get_files/file_metadata',
    {
      file_ids: JSON.stringify(imageIds),
      include_services_object: true, // default
      include_notes: false, // default
    },
  );
  const {requestId, fetch} = useFetch(url, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'Hydrus-Client-API-Session-Key': apiConfig.sessionKey,
    },
  });
  const response = await fetch;
  const data = <ImageMetadata>await response.json();
  const ratingServices = data.services;
  for (const imageMeta of data.metadata) {
    const fileIdI = imageMeta.file_id;
    preloadedMeta.value[fileIdI] = imageMeta;
    const ratings = imageMeta.ratings;
    preloadedRatings.value[fileIdI] = {};
    for (const ratingHash in ratings) {
      const ratingMeta = ratingServices[ratingHash];
      const mergedRatingData = <AnyRatingServiceIncludingValue>{
        ...ratingMeta,
        value: ratings[ratingHash],
        service_key: ratingHash,
      };
      (
        <{ [key: string]: AnyRatingServiceIncludingValue }>preloadedRatings.value[fileIdI]
      )[ratingHash] = mergedRatingData;
    }
  }
}

/**
 * Preload +/- 10 images
 * - actually just generate the source url
 * - the browser will handle the preloading via `<img>` tags.
 */
// eslint-disable-next-line prettier/prettier
watch(
  // eslint-disable-next-line prettier/prettier
  [() => lowerBound.value, () => upperBound.value, () => apiConfig.fileUrls],
  () => {
    console.log('PRELOAD');
    if (document.images && apiConfig.fileUrls) {
      const cleanupLowerStart = 0;
      const cleanupLowerEnd = Math.max(
        0,
        Math.min(lowerBound.value - 50, apiConfig.fileUrls.length),
      );
      const cleanupUpperStart = Math.max(upperBound.value + 50, 0);
      const cleanupUpperEnd = apiConfig.fileUrls.length;
      // remove images more than 50 ago
      console.log(
        `Cleanup [Lower] from ${cleanupLowerStart} - ${cleanupLowerEnd}.`,
      );
      for (let i = cleanupLowerStart; i < cleanupLowerEnd; i++) {
        const src = apiConfig.fileUrls[i].src;
        // console.log(`Cleanup [Lower] Img: ${src}.`);
        preloadedImages.value[src] = null;
      }
      // remove images more than 50 ahead
      console.log(
        `Cleanup [Upper] from ${cleanupUpperStart} - ${cleanupUpperEnd}.`,
      );
      for (let i = cleanupUpperStart; i < cleanupUpperEnd; i++) {
        const src = apiConfig.fileUrls[i].src;
        // console.log(`Cleanup [Upper] Img: ${src}.`);
        preloadedImages.value[src] = null;
      }
      // cache +/- 10 images
      const cacheStartIndex = Math.max(0, lowerBound.value - 10);
      const cacheEndIndex = Math.min(
        upperBound.value + 10,
        apiConfig.fileUrls.length,
      );
      console.log(`Cache from ${cacheStartIndex} - ${cacheEndIndex}.`);
      for (let i = cacheStartIndex; i < cacheEndIndex; i++) {
        const src = apiConfig.fileUrls[i].src;
        if (
          src in preloadedImages.value &&
          preloadedImages.value[src] !== null
        ) {
          continue;
        }

        console.log(`PRELOADING[${i}] ${apiConfig.fileUrls[i].id} = ${src}`);
        preloadedImages.value[src] = null;
        const img = new ReactiveImage();
        img.src = src;
        console.log('preloading image:', src);
        preloadedImages.value[src] = img;
      }
    }
  },
);
// eslint-disable-next-line prettier/prettier
watch(
  // eslint-disable-next-line prettier/prettier
  [() => current.value, () => upperBound.value, () => apiConfig.fileUrls],
  async () => {
    if (document.images && apiConfig.fileUrls && active.value !== undefined) {
      const missingMeta: number[] = [];
      if (apiConfig.viewerMode === 'single') {
        // always reload the current image, to make sure it's up to date.
        missingMeta.push(active.value.id);
      }
      for (
        let i = upperBound.value;
        i < upperBound.value + 10 && i < apiConfig.fileUrls.length;
        i++
      ) {
        const img = apiConfig.fileUrls[i];
        if (img.id in preloadedRatings.value) {
          continue;
        }

        preloadedRatings.value[img.id] = null;
        missingMeta.push(img.id);
        console.log('Queueing preloading meta of image', {
          i,
          id: img.id,
          url: img.src,
        });
      }

      console.log('Queueing preloading meta of image', {missingMeta});
      await loadMetaForImageIds(missingMeta);
    }
  },
);

// const vote = ref<boolean|null>(null);
function prev() {
  goto(current.value - 1);
}

function next() {
  goto(current.value + 1);
}

function clampCurrentIndex(nextIndex: number, maxIndex = apiConfig.fileUrls.length - 1): number {
  return Math.max(0, Math.min(maxIndex, nextIndex));
}

function setReasonatedIndex(nextIndex: number, reason: ReasonatedIndexReason, maxIndex?: number) {
  const clampedIndex = clampCurrentIndex(nextIndex, maxIndex);
  if (
    reasonatedIndex.value.int === clampedIndex &&
    reasonatedIndex.value.reason === reason
  ) {
    return;
  }
  reasonatedIndex.value = {
    int: clampedIndex,
    reason,
  };
}

function goto(index: number) {
  const maxIndex = (
    isScrollViewer.value && activeScrollLoaderIndex.value !== null
      ? activeScrollLoaderIndex.value
      : apiConfig.fileUrls.length - 1
  );
  setReasonatedIndex(index, 'goto', maxIndex);
}

function scrollTo(targetIndex = current.value) {
  if (apiConfig.viewerMode === 'single') {
    return;
  }
  const liElement = mediaLiElements.value[targetIndex];
  if (!liElement) {
    return;
  }
  liElement.scrollIntoView({
    behavior: 'smooth',
    block: 'nearest',
    inline: 'nearest',
  });
}

/**
 * Submit a rating service value.
 * @param file_id
 * @param key
 * @param newRating
 * @see https://hydrusnetwork.github.io/hydrus/developer_api.html#edit_ratings_set_rating
 */
async function rate(file_id: number, key: ServiceHash, newRating: AnyValue) {
  if (apiConfig.sessionKey === null) {
    throw Error("sessionKey can't be null");
  }
  await apiConfig.ensureSession();
  if (apiConfig.voteServiceIds === null) {
    throw Error("voteServiceIds can't be null");
  }
  const url = buildUrlWithParams(
    apiConfig.host + '/edit_ratings/set_rating',
    {},
  );
  const payload = {
    file_id,
    rating_service_key: key,
    rating: newRating,
  };
  const {requestId, fetch} = useFetch(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'Hydrus-Client-API-Session-Key': apiConfig.sessionKey,
    },
    body: JSON.stringify(payload),
  });
  const response = await fetch;
  if (response.status !== 200) {
    console.error('Network response was not OK', response);
    throw new Error('Network response was not OK');
  }
  if ((preloadedRatings.value[file_id] ?? null) !== null) {
    (
      <{ [key: ServiceHash]: AnyRatingServiceIncludingValue }>
        preloadedRatings.value[file_id]
    )[key].value = newRating;
  }
}

/**
 * Archive or unarchive this file.
 * @see https://hydrusnetwork.github.io/hydrus/developer_api.html#edit_ratings_set_rating
 */
async function archive(newState: boolean) {
  console.log(`Submitting archive state for ${fileId.value}: ${newState}`);
  if (apiConfig.sessionKey === null) {
    throw Error("sessionKey can't be null");
  }
  if (apiConfig.voteServiceIds === null) {
    throw Error("voteServiceIds can't be null");
  }
  const endpoint = (
    newState
      ? '/add_files/archive_files'
      : '/add_files/unarchive_files'
  );
  const url = buildUrlWithParams(apiConfig.host + endpoint, {});
  const payload = {
    // it's the same for both endpoints.
    file_id: fileId.value,
  };
  const {requestId, fetch} = useFetch(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'Hydrus-Client-API-Session-Key': apiConfig.sessionKey,
    },
    body: JSON.stringify(payload),
  });
  const response = await fetch;
  if (response.status !== 200) {
    console.error('Network response was not OK', response);
    throw new Error('Network response was not OK');
  }
  // TODO: adapt if we cache archive state.
  // if (((preloadedRatings.value[fileId.value] ?? null) !== null)) {
  //   (<{ [key: ServiceHash]: AnyRatingServiceIncludingValue }>preloadedRatings.value[fileId.value])[key].value = newRating;
  // }
}

function upvote() {
  overlayVote(true);
}

function downvote() {
  overlayVote(false);
}

/**
 * Shows the overlay.
 * @param up if we already should apply stepping in one direction (true/false = up/down),
 *           or just open it as is (null.)
 */
function overlayVote(up: boolean | null) {
  const nextRatings = cloneRatings(votableRatings.value);
  const reversedRatings = [...nextRatings];
  reversedRatings.reverse();
  let overflow: boolean | null = up;

  for (const votableRating of reversedRatings) {
    if (overflow === null) {
      // no more overflow → done
      break;
    }
    const action: boolean = overflow;
    overflow = null;

    if (isCounterServiceIncludingValue(votableRating)) {
      // 6️⃣9️⃣
      // +1 can't overflow for the counter
      if (action) {
        votableRating.value += 1;
      } else {
        votableRating.value -= 1;
      }
      if (votableRating.value < 0) {
        overflow = false; // underflow.
        votableRating.value = 0; // reset this value to min
      }
    } else if (isLikeServiceIncludingValue(votableRating)) {
      // ⬆️/⬇️
      // false, null, true
      if (votableRating.value !== action) {
        if (votableRating.value !== null) {
          // either down from up, or up from down
          // set null
          votableRating.value = null;
        } else {
          votableRating.value = action;
        }
      } else {
        overflow = action;
      }
    } else if (isStarsServiceIncludingValue(votableRating)) {
      // ⭐️⭐️⭐️⭐️⭐️
      // probably the most difficult in terms of boundaries
      const minStars = votableRating.min_stars ?? 0;
      const maxStars = votableRating.max_stars ?? 5;
      if (action) {
        if (votableRating.value === null) {
          votableRating.value = minStars;
          continue;
        }
        votableRating.value += 1;
        if (votableRating.value > maxStars) {
          overflow = true;
          votableRating.value = maxStars;
        }
      } else {
        if (votableRating.value === null) {
          continue;
        }
        votableRating.value -= 1;
        if (votableRating.value < minStars) {
          overflow = false;
          votableRating.value = null;
        }
      }
    }
  }

  votableRatings.value = nextRatings;
  showVoteOverlay.value = true;
}

async function submitVote() {
  if (isSubmittingVote.value) {
    return;
  }
  const currentCurrent = current.value;
  // sanity check
  if (apiConfig.sessionKey === null) {
    throw Error("sessionKey can't be null");
  }
  isSubmittingVote.value = true;
  // copy the payload to make sure it's not going to change accidentally.
  type Payloads = {
    file_id: number;
    rating_service_key: ServiceHash;
    rating: AnyValue;
  };
  const payloads: Payloads[] = JSON.parse(JSON.stringify(
    votableRatings.value.map((vote) => ({
      file_id: fileId.value,
      rating_service_key: vote.service_key,
      rating: vote.value,
    })),
  ));

  try {
    // send each of the votes
    console.log('submitVote()', payloads);
    // TODO: use existing `rate(…)`function.
    const url = apiConfig.host + '/edit_ratings/set_rating';
    for (const payload of payloads) {
      const {requestId, fetch} = useFetch(url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'Hydrus-Client-API-Session-Key': apiConfig.sessionKey,
        },
        body: JSON.stringify(payload),
      });
      const response = await fetch;
      if (response.status !== 200) {
        console.error('Network response was not OK', payload, response);
        await loadMetaForImageIds(payloads.map((payload) => payload.file_id));
        throw new Error(
          `submitVote(${payload.file_id}): Network response was not OK`,
        );
      }
    }

    // send archival state if needed
    const archiveDecision = computeArchivalDecision(apiConfig.archivalMode, votableRatings.value, apiConfig.customArchivalRules);
    console.log(`Archive decision for ${fileId.value}: ${archiveDecision}`);
    if (archiveDecision !== null) {
      await archive(archiveDecision);
    }

    // aftercare
    console.log('submitVote', current.value);
    if (current.value === currentCurrent) {
      // check first that we didn't move to another page in the meantime.
      cancelVote(); // closes and deletes stuff
      next(); // next image, for your convince
      await loadMetaForImageIds(payloads.map((payload) => payload.file_id));
    }
  } finally {
    isSubmittingVote.value = false;
  }
}

function cancelVote() {
  showVoteOverlay.value = false;
  writeVotableRatings();
}

async function sendTagAddition(file_id: number, tag: string) {
  await sendTagChange(tag, TagApiAction.ADD);
  await loadMetaForImageIds([file_id]);
}

async function sendTagDeletion(file_id: number, tag: string) {
  if (confirm(`Are you sure you wanna delete the tag "${tag}"?`)) {
    await sendTagChange(tag, TagApiAction.DELETE);
    await loadMetaForImageIds([file_id]);
  }
}

async function sendTagChange(
  tag: string,
  action: TagApiAction,
): Promise<boolean> {
  if (apiConfig.sessionKey === null) {
    throw Error("sessionKey can't be null");
  }
  if (!apiConfig.tagWriteServiceId) {
    throw Error('tagWriteServiceId must be set');
  }
  const payload = {
    file_id: fileId.value,
    // basically it's `service_keys_to_actions_to_tags[service][action] = tag`
    service_keys_to_actions_to_tags: {
      [apiConfig.tagWriteServiceId]: {[action]: [tag]},
    },
  };
  const url = buildUrlWithParams(apiConfig.host + '/add_tags/add_tags', {});
  const {requestId, fetch} = useFetch(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'Hydrus-Client-API-Session-Key': apiConfig.sessionKey,
    },
    body: JSON.stringify(payload),
  });
  const response = await fetch;
  return response.status === 200;
}

function isSameViewerRouteContext(
  left: ViewerRouteStateSnapshot,
  right: ViewerRouteStateSnapshot,
): boolean {
  return left.page === right.page && left.search === right.search;
}

function isSequentialViewerRouteStep(
  previousState: ViewerRouteStateSnapshot | null,
  nextState: ViewerRouteStateSnapshot,
): boolean {
  if (previousState === null) {
    return false;
  }
  return (
    isSameViewerRouteContext(previousState, nextState) &&
    Math.abs(nextState.index - previousState.index) === 1
  );
}

function resetViewerRouteCheckpoint(
  state: ViewerRouteStateSnapshot,
  timestamp = Date.now(),
) {
  lastViewerRouteCheckpointState = state;
  lastViewerRouteCheckpointAt = timestamp;
  viewerRouteImagesSinceCheckpoint = 0;
}

async function pushViewerRouteState(
  state: ViewerRouteStateSnapshot,
  query = buildViewerRouteQuery(route.query, state),
  timestamp = Date.now(),
) {
  if (shouldSkipViewerRouteSync.value) {
    return;
  }

  const currentQueryKey = getViewerRouteQueryKey(route.query);
  const nextQueryKey = getViewerRouteQueryKey(query);
  if (currentQueryKey === nextQueryKey) {
    resetViewerRouteCheckpoint(state, timestamp);
    return;
  }

  try {
    await router.push({
      name: 'home',
      query,
    });
    resetViewerRouteCheckpoint(state, timestamp);
  } catch (error) {
    console.error('Failed to push viewer state into route history.', error);
  }
}

async function waitForViewerRouteRestore(
  predicate: () => boolean,
  timeoutSeconds = 15,
): Promise<boolean> {
  const startedAt = Date.now();

  while (!predicate()) {
    if (Date.now() - startedAt >= timeoutSeconds * 1000) {
      return false;
    }
    await sleep(0.05);
  }

  return true;
}

async function findIndexByImageHash(hashId: string): Promise<number | null> {
  const normalizedHashId = hashId.trim().toLowerCase();
  if (normalizedHashId === '') {
    return null;
  }

  const findLoadedIndex = () =>
    apiConfig.fileUrls.findIndex((source) =>
      preloadedMeta.value[source.id]?.hash?.toLowerCase() === normalizedHashId,
    );

  const alreadyLoadedIndex = findLoadedIndex();
  if (alreadyLoadedIndex !== -1) {
    return alreadyLoadedIndex;
  }

  const missingMetadataIds = apiConfig.fileUrls
    .map((source) => source.id)
    .filter((imageId) => preloadedMeta.value[imageId] === undefined);

  for (let start = 0; start < missingMetadataIds.length; start += 200) {
    const metadataBatchIds = missingMetadataIds.slice(start, start + 200);
    if (metadataBatchIds.length === 0) {
      continue;
    }
    await loadMetaForImageIds(metadataBatchIds);
    const matchedIndex = findLoadedIndex();
    if (matchedIndex !== -1) {
      return matchedIndex;
    }
  }

  return null;
}

async function restoreInitialViewerRouteState() {
  if (initialViewerRouteState === null) {
    return;
  }

  shouldSkipViewerRouteSync.value = true;

  try {
    const pagesLoaded = await waitForViewerRouteRestore(() => apiConfig.pages !== null);
    if (!pagesLoaded) {
      return;
    }

    const resolvedPage = resolveSelectedPageFromRouteToken(
      initialViewerRouteState.page,
      apiConfig.pages,
    );
    if (resolvedPage !== undefined) {
      apiConfig.selectedPage = resolvedPage;
    } else if (
      initialViewerRouteState.page === null &&
      initialViewerRouteState.search !== ''
    ) {
      apiConfig.selectedPage = SpecialPage.SEARCH;
    }
    if (
      initialViewerRouteState.search !== '' ||
      initialViewerRouteState.page !== null
    ) {
      apiConfig.searchQuery = initialViewerRouteState.search;
    }

    const selectionLoaded = await waitForViewerRouteRestore(
      () => loadedViewerSelectionSignature.value === currentViewerSelectionSignature.value,
    );
    if (!selectionLoaded) {
      return;
    }

    let restoredIndex: number | null = null;
    if (initialViewerRouteState.hash !== null) {
      restoredIndex = await findIndexByImageHash(initialViewerRouteState.hash);
    }
    if (restoredIndex === null && initialViewerRouteState.index !== null) {
      restoredIndex = initialViewerRouteState.index;
    }
    if (restoredIndex !== null) {
      goto(restoredIndex);
    }
  } finally {
    shouldSkipViewerRouteSync.value = false;
    const restoredState = viewerRouteState.value;
    if (restoredState !== null) {
      lastObservedViewerRouteState = restoredState;
      resetViewerRouteCheckpoint(restoredState);
    }
  }
}

async function reset() {
  const api = useApiStore();
  setReasonatedIndex(0, 'goto');
  lowerBound.value = 0;
  upperBound.value = 0;
  api.reset();
  preloadedMeta.value = {};
  committedScrollMeta.value = {};
  preloadedImages.value = {};
  preloadedRatings.value = {};
  scrollSlideReady.value = {};
  skippedScrollLoaderIds.value = [];
  await api.refreshAndLoadFileIds();
}

async function reload() {
  const api = useApiStore();
  setReasonatedIndex(0, 'goto');
  lowerBound.value = 0;
  upperBound.value = 0;
  preloadedMeta.value = {};
  committedScrollMeta.value = {};
  preloadedImages.value = {};
  preloadedRatings.value = {};
  scrollSlideReady.value = {};
  skippedScrollLoaderIds.value = [];

  try {
    await api.loadFileIds();
  } catch (error) {
    // Check if this is a network error
    if (isNetworkError(error)) {
      // Show error toast with retry capability
      networkErrorMessage.value = 'Failed to connect to the host';
      networkErrorDetails.value = getErrorMessage(error);

      // Create retry policy with user's settings
      currentRetryPolicy.value = new RetryPolicy({
        maxAttempts: api.retryMaxAttempts,
        initialDelay: api.retryInitialDelay,
      });

      // Show the error toast if auto-retry is enabled
      if (api.retryAutoEnabled) {
        networkErrorToastVisible.value = true;
      } else {
        // If auto-retry is disabled, just show error message and allow manual retry
        networkErrorToastVisible.value = true;
      }
    } else {
      // Not a network error, re-throw it
      throw error;
    }
  }
}

function onKeyPress(event: KeyboardEvent) {
  if (event.target instanceof Element) {
    const target = event.target;
    const tag = target.tagName.toLowerCase();
    if (['input', 'textarea', 'button', 'a'].indexOf(tag) !== -1) {
      console.log('Not handling global key listener: input element detected by name.');
      return;
    }
    if (target.closest('[local-keyevents]')) {
      console.log('Not handling global key listener: element (or parent) detected having [local-keyevents] attribute.');
      return;
    }
  }

  if (
    showVoteOverlay.value &&
    isSubmittingVote.value &&
    [Key.ArrowLeft, Key.ArrowRight, Key.ArrowUp, Key.ArrowDown, Key.Enter, Key.Escape, Key.Shift, '-'].includes(event.key)
  ) {
    event.preventDefault();
    return;
  }

  if (event.key === Key.ArrowLeft) {
    event.preventDefault();
    prev();
  } else if (event.key === Key.ArrowRight) {
    event.preventDefault();
    next();
  } else if (event.key === Key.ArrowUp) {
    event.preventDefault();
    upvote();
  } else if (event.key === Key.ArrowDown) {
    event.preventDefault();
    downvote();
  } else if (event.key === Key.Enter) {
    event.preventDefault();
    if (showVoteOverlay.value) {
      submitVote();
    } else {
      overlayVote(null);
    }
  } else if (event.key === Key.Escape || event.key === Key.Shift || event.key === '-') {
    event.preventDefault();
    if (showVoteOverlay.value) {
      cancelVote();
    } else {
      overlayVote(null);
    }
  } else if (event.key === 'h') {
    showUrlsAndVotes.value = !showUrlsAndVotes.value;
  } else if (event.key === 'i') {
    showRequestStats.value = !showRequestStats.value;
  } else if (event.key === 't') {
    showBigTagAdd.value = !showBigTagAdd.value;
  } else if (event.key === 'f') {
    fullscreen();
  } else if (event.key === 'p') {
    autoplay.value = !autoplay.value;
  }
}

watch(
  () => apiConfig.fileUrls.map((source) => source.id).join(','),
  () => {
    loadedViewerSelectionSignature.value = currentViewerSelectionSignature.value;
  },
  {immediate: true},
);

watch(
  () => currentViewerSelectionSignature.value,
  () => {
    if (apiConfig.fileUrls.length === 0) {
      loadedViewerSelectionSignature.value = currentViewerSelectionSignature.value;
    }
  },
);

watch(
  () => viewerRouteState.value,
  (nextState) => {
    if (
      shouldSkipViewerRouteSync.value ||
      nextState === null ||
      loadedViewerSelectionSignature.value !== currentViewerSelectionSignature.value
    ) {
      return;
    }

    if (isSequentialViewerRouteStep(lastObservedViewerRouteState, nextState)) {
      viewerRouteImagesSinceCheckpoint += Math.abs(
        nextState.index - (lastObservedViewerRouteState?.index ?? nextState.index),
      );
    }
    lastObservedViewerRouteState = nextState;

    const nextQuery = buildViewerRouteQuery(route.query, nextState);
    const currentQueryKey = getViewerRouteQueryKey(route.query);
    const nextQueryKey = getViewerRouteQueryKey(nextQuery);
    const now = Date.now();

    if (currentQueryKey === nextQueryKey) {
      if (lastViewerRouteCheckpointState === null) {
        resetViewerRouteCheckpoint(nextState, now);
      }
      return;
    }

    const shouldPushImmediately = (
      !apiConfig.reducedHistoryStepsEnabled ||
      lastViewerRouteCheckpointState === null ||
      !isSequentialViewerRouteStep(lastViewerRouteCheckpointState, nextState)
    );
    if (shouldPushImmediately) {
      pushViewerRouteState(nextState, nextQuery, now);
      return;
    }

    const timeCheckpointReached = (
      apiConfig.historyCheckpointSeconds <= 0 ||
      now - lastViewerRouteCheckpointAt >= apiConfig.historyCheckpointSeconds * 1000
    );
    const imageCheckpointReached = (
      apiConfig.historyCheckpointImages <= 1 ||
      viewerRouteImagesSinceCheckpoint >= apiConfig.historyCheckpointImages
    );

    if (timeCheckpointReached || imageCheckpointReached) {
      pushViewerRouteState(nextState, nextQuery, now);
    }
  },
  {
    immediate: true,
    deep: true,
  },
);

onMounted(() => {
  w.on('keydown.image-slideshow', onKeyPress);
  (async () => {
    await reload();
    await restoreInitialViewerRouteState();
  })();
});
onUnmounted(() => {
  w.off('keydown.image-slideshow');
});

function mediaGotClicked() {
  zoomed.value = true;
  if (apiConfig.mouseVotes) {
    // if the double click is active, don't change image size.

  }
}

const isScrollViewer = computed(() => apiConfig.viewerMode !== 'single');
const topBarVisibility = computed(() =>
  resolveBarVisibility(apiConfig.viewerMode, toolbarStore.topBarVisibility),
);
const bottomBarVisibility = computed(() =>
  resolveBarVisibility(apiConfig.viewerMode, toolbarStore.bottomBarVisibility),
);
const horizontalScrollOptions = computed(() =>
  apiConfig.viewerMode === 'desktop' ? {} : false,
);
const scrollGestureMode = computed(() =>
  apiConfig.viewerMode === 'mobile' ? false : 'both',
);
const showInlineTopBar = computed(() =>
  isScrollViewer.value &&
  topBarVisibility.value === 'always' &&
  toolbarStore.topItems.length > 0,
);
const showInlineBottomBar = computed(() =>
  isScrollViewer.value &&
  bottomBarVisibility.value === 'always' &&
  toolbarStore.bottomItems.length > 0,
);
const showFloatingTopBar = computed(() =>
  isScrollViewer.value &&
  topBarVisibility.value !== 'always' &&
  toolbarStore.topItems.length > 0,
);
const showFloatingBottomBar = computed(() =>
  isScrollViewer.value &&
  bottomBarVisibility.value !== 'always' &&
  toolbarStore.bottomItems.length > 0,
);
const topFloatingMode = computed(() => topBarVisibility.value === 'always' ? 'fixed' : topBarVisibility.value);
const bottomFloatingMode = computed(() => bottomBarVisibility.value === 'always' ? 'fixed' : bottomBarVisibility.value);

type ObservedSlideElement = {
  fileIndex: number;
  element: HTMLLIElement;
};

const mediaLiLiRefs = ref<MediaMetaSlideGroupExposed[]>([]);
const mediaLiElements = computed<(HTMLLIElement | undefined)[]>(() =>
  mediaLiLiRefs.value.map(
    (group) => unref(group?.liMediaRef) as (HTMLLIElement | undefined) || undefined,
  ),
);
const observedSlideElements = computed<ObservedSlideElement[]>(() =>
  mediaLiLiRefs.value.flatMap((group, fileIndex) => {
    const elements = [unref(group?.liMediaRef), unref(group?.liMetaRef)].filter((element): element is HTMLLIElement => element instanceof HTMLLIElement);

    return elements.map((element) => ({
      fileIndex,
      element,
    }));
  }),
);

const imageVisibilityFlags = ref<boolean[]>([]);
const imageNearViewportFlags = ref<boolean[]>([]);
const imageActiveFlags = ref<boolean[]>([]);
const visibleForLoadingElements = new Set<Element>();
const nearViewportElements = new Set<Element>();
const activeObservedElements = new Set<Element>();
const scrollViewerStyle = computed(() => ({
  // Hover-only bars float above content and don't reserve space; only offset for fixed/autohide.
  '--scroll-top-bar-offset': `${showFloatingTopBar.value && topBarVisibility.value !== 'hover' ? scrollTopBarHeight.value : 0}px`,
  '--scroll-bottom-bar-offset': `${showFloatingBottomBar.value && bottomBarVisibility.value !== 'hover' ? scrollBottomBarHeight.value : 0}px`,
}));

function buildObservedFlagArray(intersectingElements: Set<Element>): boolean[] {
  const flags = new Array<boolean>(apiConfig.fileUrls.length).fill(false);

  observedSlideElements.value.forEach(({fileIndex, element}) => {
    if (intersectingElements.has(element)) {
      flags[fileIndex] = true;
    }
  });

  return flags;
}

function isScrollSlideNearViewport(index: number): boolean {
  return isScrollViewer.value && (
    imageNearViewportFlags.value[index] === true ||
    imageVisibilityFlags.value[index] === true ||
    imageActiveFlags.value[index] === true ||
    Math.abs(current.value - index) <= 1
  );
}

function setScrollSlideReady(fileId: number, ready: boolean) {
  if (!ready) return;
  scrollSlideReady.value[fileId] = true;
}

function skipScrollLoader(fileId: number) {
  if (!skippedScrollLoaderIds.value.includes(fileId)) {
    skippedScrollLoaderIds.value = [...skippedScrollLoaderIds.value, fileId];
  }
}

const skippedScrollLoaderIdSet = computed<Set<number>>(() =>
  new Set(skippedScrollLoaderIds.value),
);

watchEffect(() => {
  if (!isScrollViewer.value) return;

  apiConfig.fileUrls.forEach((source, index) => {
    if (!isScrollSlideNearViewport(index)) return;

    const resolvedMeta = preloadedMeta.value[source.id];
    if (!resolvedMeta) return;

    committedScrollMeta.value[source.id] = resolvedMeta;
  });
});

const activeScrollLoaderIndex = computed<number | null>(() => {
  if (!isScrollViewer.value) return null;

  for (let index = 0; index < apiConfig.fileUrls.length; index += 1) {
    const source = apiConfig.fileUrls[index];
    if (scrollSlideReady.value[source.id] === true) continue;
    if (skippedScrollLoaderIdSet.value.has(source.id)) continue;
    return index;
  }

  return null;
});

const scrollRenderableSources = computed(() => {
  if (!isScrollViewer.value) return apiConfig.fileUrls;
  if (activeScrollLoaderIndex.value === null) return apiConfig.fileUrls;
  return apiConfig.fileUrls.slice(0, activeScrollLoaderIndex.value + 1);
});

watch(
  () => apiConfig.fileUrls.map((source) => source.id).join(','),
  () => {
    committedScrollMeta.value = {};
    scrollSlideReady.value = {};
    skippedScrollLoaderIds.value = [];
  },
);

function onTopFloatingBarHeightChange(value: number) {
  scrollTopBarHeight.value = value;
}

function onBottomFloatingBarHeightChange(value: number) {
  scrollBottomBarHeight.value = value;
}

function clampRevealAmounts() {
  scrollTopBarRevealPx.value = Math.max(0, Math.min(scrollTopBarRevealPx.value, scrollTopBarHeight.value));
  scrollBottomBarRevealPx.value = Math.max(0, Math.min(scrollBottomBarRevealPx.value, scrollBottomBarHeight.value));
}

function applyScrollBarRevealDelta(deltaY: number) {
  if (!isScrollViewer.value) return;

  if (topBarVisibility.value === 'autohide') {
    scrollTopBarRevealPx.value -= deltaY;
  }
  if (bottomBarVisibility.value === 'autohide') {
    scrollBottomBarRevealPx.value += deltaY;
  }

  clampRevealAmounts();
}

function onScrollGestureWheel(event: WheelEvent) {
  applyScrollBarRevealDelta(event.deltaY);
}

function onScrollGestureTouchStart(event: TouchEvent) {
  if (event.touches.length !== 1) return;
  lastScrollTouchY.value = event.touches[0].clientY;
}

function onScrollGestureTouchMove(event: TouchEvent) {
  if (event.touches.length !== 1 || lastScrollTouchY.value === null) return;
  const nextY = event.touches[0].clientY;
  applyScrollBarRevealDelta(lastScrollTouchY.value - nextY);
  lastScrollTouchY.value = nextY;
}

function onScrollGestureTouchEnd() {
  lastScrollTouchY.value = null;
}

watch(
  [() => topBarVisibility.value, () => bottomBarVisibility.value, () => scrollTopBarHeight.value, () => scrollBottomBarHeight.value],
  () => {
    scrollTopBarRevealPx.value =
      topBarVisibility.value === 'fixed' ? scrollTopBarHeight.value : 0;
    scrollBottomBarRevealPx.value =
      bottomBarVisibility.value === 'fixed' ? scrollBottomBarHeight.value : 0;
    clampRevealAmounts();
  },
  {immediate: true},
);

watch(
  () => reasonatedIndex.value,
  (nextIndex, previousIndex) => {
    if (!isScrollViewer.value) return;
    if (nextIndex.reason !== 'goto') return;
    if (previousIndex && nextIndex.int === previousIndex.int && nextIndex.reason === previousIndex.reason) {
      return;
    }
    scrollTo(nextIndex.int);
  },
);

watch(
  () => activeScrollLoaderIndex.value,
  (index) => {
    if (!isScrollViewer.value) return;
    if (index === null) return;
    if (current.value <= index) return;
    setReasonatedIndex(index, 'goto');
  },
  {immediate: true},
);

const activeIndexes = computed<number[]>(() => imageActiveFlags.value
  .map((value, index) => value ? index : null)
  .filter((value) => value !== null) as number[],
);

const scrollIndex = computed<number>(() => {
  // we know that it is sorted.
  let middle: number[];
  if (activeIndexes.value.length === 0) {
    middle = [current.value];
  } else if (activeIndexes.value.length === 1) {
    // the 'length % 2 == 0' fails for length == 2, so we cover it here.
    middle = activeIndexes.value;
  } else if (activeIndexes.value.length % 2 == 0) {
    // If length is even -> we have 2 middle elements
    // note, this returns an empty array for when the length is 2 (or less), hence this is covered before.
    middle = activeIndexes.value.slice((activeIndexes.value.length / 2) - 1, (activeIndexes.value.length / 2) + 1);
  } else {
    // If length is odd -> we have 1 middle element
    middle = activeIndexes.value.slice((activeIndexes.value.length / 2) - 1, (activeIndexes.value.length / 2) + 2);
  }
  let index = middle.reduce((sum, curr) => sum + curr, 0) / middle.length;
  index = Math.floor(index);
  index = Math.max(0, index);
  return index;
});

// change `current` based on `scrollIndex` changes.
// NEW: with the Math.max(…) we make sure we will not end up with -1 (settings page) on the single mode which doesn't has that..
watch(
  () => ({
    index: scrollIndex.value,
    currentIndex: reasonatedIndex.value,
  }),
  (args) => {
    const nextIndex = clampCurrentIndex(args.index);
    if (
      args.currentIndex.reason === 'goto' &&
      nextIndex !== args.currentIndex.int
    ) {
      return;
    }
    setReasonatedIndex(nextIndex, 'scroll');
  },
);

// change `lowerBound` and `upperBound` based on `activeIndexes` changes.
// in other words, make them reflect the currently visible element.
// NEW: with the Math.max(…) we make sure we will not end up with -1 (settings page) on the single mode which doesn't has that..
watch(
  () => ({
    indexes: activeIndexes.value,
    current: current.value,
    viewerMode: apiConfig.viewerMode,
  }),
  (args) => {
    if (args.viewerMode === 'single') {
      lowerBound.value = args.current;
      upperBound.value = args.current;
      return;
    }
    let lower = Infinity;
    let upper = -Infinity;
    args.indexes.forEach((n: number) => {
      lower = Math.min(lower, n);
      upper = Math.max(upper, n);
    });
    if (lower !== Infinity) {
      lowerBound.value = lower;
    }
    if (upper !== -Infinity) {
      upperBound.value = upper;
    }
  },
);

// Wide-margin observer: marks items within 1024px as "visible" so they preload early.
const outOfBoundsObserverForLoading = new IntersectionObserver(
  (entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        visibleForLoadingElements.add(entry.target);
      } else {
        visibleForLoadingElements.delete(entry.target);
      }
    });

    imageVisibilityFlags.value = buildObservedFlagArray(visibleForLoadingElements);
  },
  {
    rootMargin: '1024px', // not specifying 2 values means x + y, hence we have to not write different code for display/mobile.
  },
);

const nearViewportObserver = new IntersectionObserver(
  (entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        nearViewportElements.add(entry.target);
      } else {
        nearViewportElements.delete(entry.target);
      }
    });

    imageNearViewportFlags.value = buildObservedFlagArray(nearViewportElements);
  },
  {
    rootMargin: '256px',
  },
);

// Tight observer: only marks items as "active" when they are at least 50% on screen.
// Used for scrollIndex / current so that prev/next navigation is accurate.
const activeElementObserver = new IntersectionObserver(
  (entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        activeObservedElements.add(entry.target);
      } else {
        activeObservedElements.delete(entry.target);
      }
    });

    imageActiveFlags.value = buildObservedFlagArray(activeObservedElements);
  },
  {
    threshold: 0.5, // item must be ≥50% visible to count as the active slide
  },
);

watch(() => observedSlideElements.value, (elementList) => {
  // note, as it won't deepcopy the list params, newList === oldList at all times. Hence, we can't do any cool diffing.
  const existingElementList = elementList.map(({element}) => element);

  outOfBoundsObserverForLoading.disconnect();
  nearViewportObserver.disconnect();
  activeElementObserver.disconnect();
  visibleForLoadingElements.clear();
  nearViewportElements.clear();
  activeObservedElements.clear();
  existingElementList.forEach((li) => {
    outOfBoundsObserverForLoading.observe(li);
    nearViewportObserver.observe(li);
    activeElementObserver.observe(li);
  });
  imageVisibilityFlags.value = new Array<boolean>(apiConfig.fileUrls.length).fill(false);
  imageNearViewportFlags.value = new Array<boolean>(apiConfig.fileUrls.length).fill(false);
  imageActiveFlags.value = new Array<boolean>(apiConfig.fileUrls.length).fill(false);
  console.log(`imageListRef - observing: ${existingElementList.length}`);
}, {deep: true});
const videoRef = ref<null | HTMLVideoElement>(null);
const src = computed(() => active.value?.src);
useTrackedVideo(videoRef, src);

</script>

<style lang="scss" scoped>
html,
body,
.root.viewer-single {
  width: 100%;
  height: 100%;
  overflow: hidden;
  padding: 0;
  margin: 0;
  background: var(--bg-base);
}

$min_px_width_for_desktop_layout: 800px;

.root.viewer-single {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: auto minmax(0, 1fr) auto;
  width: 100%;
  height: 100%;
  min-height: 100dvh;

  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  top: env(safe-area-inset-top);
  left: env(safe-area-inset-left);
  right: env(safe-area-inset-right);
  bottom: env(safe-area-inset-bottom);
  position: absolute;
}

.root.viewer-desktop {
  &, .image-wrapper, .image-wrapper-inner, ul.image-slides, ul.image-slides > li {
    height: 100%;
  }

  ul.image-slides {
    flex-direction: row;
    display: flex;

    .slide-toolbar {
      width: min(36rem, 92vw);
      height: 100%;
      overflow-y: auto;
      padding: 1.5rem;
      box-sizing: border-box;
      display: flex;
      align-items: flex-start;
    }
  }

}

.root.viewer-mobile {
  &, .image-wrapper, .image-wrapper-inner, ul.image-slides, ul.image-slides > li {
    // is this unused?
    width: 100%;
  }

  ul.image-slides {
    li {
      display: inline;
      flex-shrink: 0;
    }

    // Inline toolbar slide

    .slide-toolbar {
      width: 100vw;
      height: auto;
      overflow-y: auto;
      padding: 1rem;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      align-items: stretch;
      justify-content: center;
      // The bar inside should fill available width and wrap its buttons

      .buttons {
        flex-wrap: wrap;
        height: auto;
      }
    }
  }
}

.root.viewer-desktop, .root.viewer-mobile {
  --scroll-top-bar-offset: 0px;
  --scroll-bottom-bar-offset: 0px;
  /* Suppress browser swipe-navigation and pull-to-refresh gestures */
  overscroll-behavior-x: contain;
  overscroll-behavior-y: contain;

  ul.image-slides {
    flex-direction: row;
    padding: var(--scroll-top-bar-offset) 0 var(--scroll-bottom-bar-offset);
    scroll-padding-top: var(--scroll-top-bar-offset);
    scroll-padding-bottom: var(--scroll-bottom-bar-offset);
    margin: 0;
    list-style: none;
    box-sizing: border-box;
    overscroll-behavior-x: contain;

    li {
      list-style-type: none;
      flex-shrink: 0;

      &.slide-toolbar > div {
        display: flex;
        width: 100%;
      }
    }
  }
}

.sidebar-overlay-wrapper {
  pointer-events: none;
  position: absolute;
  top: 10px;
  bottom: 10px;
  left: 0;
  right: 0;
  z-index: 2;
}

.viewer-single .sidebar-overlay-side {
  pointer-events: none;
  position: relative;
  max-height: 100%;
  max-width: 50%;
  overflow-x: scroll;
  -ms-overflow-style: none; // Internet Explorer 10+
  scrollbar-width: none; // Firefox
  display: block;
  -webkit-overflow-scrolling: touch; // use single finger for scroll on ipad, not both

  &::-webkit-scrollbar {
    // Safari
    width: 0;
    height: 0;
    display: none;
  }

  scrollbar-color: transparent transparent; // iOS

  // this element (including the scrollbar!) is almost fully see-through
  opacity: 0;

  &:hover {
    // on hover: this element (including the scrollbar!) is solid.
    opacity: 1;
    pointer-events: auto; // needed for safari to scroll

    // restore scrollbar
    -ms-overflow-style: scrollbar; // Internet Explorer 10+
    scrollbar-width: thin; // Firefox

    &::-webkit-scrollbar {
      // Safari
      width: auto;
      height: auto;
      display: block;
    }

    scrollbar-color: auto; // iOS
    -webkit-overflow-scrolling: auto;

    .sidebar-overlay {
      opacity: 0.1; // all subelements are now almost fully see-through

      &:hover {
        pointer-events: auto; // needed for safari to scroll
        // on hover: except the sub element where the mouse is on.
        opacity: 1;
      }
    }
  }

  &.right {
    float: right;
    right: 10px;
  }

  &.left {
    float: left;
    left: 10px;
  }
}

.sidebar-overlay {
  background: var(--bg-surface);
  color: var(--text-primary);
  z-index: 3;
  pointer-events: auto; // needed for chrome to scroll
  padding: 10px;

  transition: opacity 0.1s;

  margin: 10px;
}

/* Mobile: restructure layout for vertical scroll with URL above image */
@media (max-width: 640px) {
  .viewer-mobile .image-wrapper {
    display: flex;
    flex-direction: column;
  }

  .viewer-mobile .sidebar-overlay-wrapper {
    /* In mobile vertical scroll, reposition URL section above image */
    position: static;
    display: flex;
    flex-direction: column;
    width: 100%;
    height: auto;
    max-height: 50vh;
    top: 0;
    bottom: auto;
    left: 0;
    right: 0;
    z-index: 2;
    order: -1;
  }

  .viewer-mobile .sidebar-overlay-side {
    position: static !important;
    max-width: 100% !important;
    max-height: 25vh;
    width: 100% !important;
    float: none !important;
    left: auto !important;
    right: auto !important;
    margin: 0 !important;
    overflow-y: auto;
    opacity: 1 !important;
    pointer-events: auto !important;

    &.left {
      order: -1;
    }

    &.right {
      order: 1;
    }

    &:hover {
      opacity: 1;

      .sidebar-overlay {
        opacity: 1 !important;
      }
    }
  }

  .viewer-mobile .sidebar-overlay {
    padding: 8px;
    margin: 4px;
    font-size: 0.85rem;
    opacity: 1 !important;
  }
}

.fetch-tracker {
  display: flex;
  flex-wrap: wrap;
  align-items: center;

  ::v-deep(.request) {
    max-width: 20em;
    flex-grow: 0;
  }
}

.tags.tag-add {
  position: relative;
  min-width: 100px;

  display: flex;
  align-items: stretch;
  align-content: stretch;
}

/* Single-view bars stretch across the viewer and get explicit placement classes. */
.viewer-single .single-toolbar-slot {
  width: 100%;
  min-width: 0;
  z-index: 3;
}

.viewer-single .single-toolbar-slot-top {
  grid-row: 1;
}

.viewer-single .single-toolbar-slot-bottom {
  grid-row: 3;
}

.viewer-single .buttons {
  display: flex;
  width: 100%;
  overflow-y: visible; /* must be visible so teleported modals/dropdowns aren't clipped */
  min-width: 0;
  z-index: 3;
}

.viewer-mobile .buttons {
  width: 100%;
  height: auto;
  overflow-x: auto;
}

.viewer-desktop .buttons {
  width: auto;
  height: 100%;
  overflow-x: auto;
}

/**
limited-by-space: works, good.
scale-to-fit: works, good.
width-fit: doesn't scale up always, it's not enforcing the width to be always 100% - it should cause overflow to the bottom
height-fit: doesn't scale up, it's not enforcing the height - it should cause overflow to the right.
original: does scale up a bit, but cuts off the top of the image a bit.
 */

.viewer-single .image-wrapper {
  position: relative;
  grid-row: 2;
  z-index: 1;
  background: var(--bg-base);
  width: 100%;
  min-height: 0; /* allow flex shrink past content height */
  -webkit-overflow-scrolling: touch; // use single finger for scroll on ipad, not both

  .image-wrapper-inner {
    width: 100%;
    height: 100%;

    img, video {
      display: block;
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
    }
  }

  .single-media-placeholder {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    pointer-events: none;
  }

  .single-media-loader {
    position: absolute;
    inset: 0;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    color: rgba(255, 255, 255, 0.9);
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
    pointer-events: none;
  }

  .single-media-loader-text {
    font-size: 0.9rem;
  }

  .single-media-unavailable {
    color: rgba(255, 255, 255, 0.8);
    font-size: 0.95rem;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
  }

  .single-media-spinner {
    display: inline-block;
    width: 32px;
    height: 32px;
    border: 3px solid rgba(255, 255, 255, 0.15);
    border-top-color: var(--accent, #7f7);
    border-radius: 50%;
    animation: single-media-spin 0.8s linear infinite;
  }

  &.limited-by-space {
    overflow: hidden;

    .image-wrapper-inner {
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    .image-wrapper-inner img,
    .image-wrapper-inner video {
      width: auto;
      height: auto;
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
    }
  }

  &.scale-to-fit {
    overflow: hidden;

    .image-wrapper-inner img,
    .image-wrapper-inner video {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  &.width-fit {
    overflow-x: hidden;
    overflow-y: auto;

    .image-wrapper-inner {
      width: 100%;
      height: auto;
      display: block;
    }

    .image-wrapper-inner img,
    .image-wrapper-inner video {
      width: 100%;
      height: auto;
      display: block;
      object-fit: contain;
    }
  }

  &.height-fit {
    overflow-x: auto;
    overflow-y: hidden;

    .image-wrapper-inner {
      width: auto;
      height: 100%;
      display: flex;
      justify-content: start;
      align-items: center;
    }

    .image-wrapper-inner img,
    .image-wrapper-inner video {
      display: block;
      max-width: 200000000000%;
      min-height: 100%;
    }
  }

  &.original {
    overflow: auto;

    .image-wrapper-inner {
      display: block;
      width: auto;
      height: auto;
      min-width: max-content;
      min-height: max-content;
    }

    .image-wrapper-inner img,
    .image-wrapper-inner video {
      display: block;
      width: auto;
      height: auto;
      object-fit: unset;
    }
  }

  .fetch-tracker-overlay {
    position: absolute;
    // top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    max-height: 100%;
    z-index: 100;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    overflow-x: auto;

    .fetch-tracker {
      padding: 1px;

      ::v-deep(.container) {
        min-width: 80%;
      }
    }
  }
}

@keyframes single-media-spin {
  to {
    transform: rotate(360deg);
  }
}

.zoom-overlay {
  z-index: 10;
}

.vote-overlay {
  z-index: 15;
}

.big-tag-add-overlay {
  z-index: 20;
}

.vote-overlay,
.big-tag-add-overlay {
  .viewer-mobile &,
  .viewer-desktop & {
    position: fixed;
  }
}
</style>
